import PropTypes from "prop-types";
import React from "react";

import { classNames } from "@swa-ui/string";

import { adjoinPropTypes } from "../Adjoin";
import { PlayList } from "../PlayList";
import styles from "./Show.module.scss";

/**
 * Show will render content, typically an overlay, by transitioning it with an animation. Show should be provided a
 * location from where the overlay will expand from. For example, when Show is rendered "below", the content will grow
 * slightly down when its rendered.
 */

export const Show = React.forwardRef((props, ref) => {
  const { children, className, duration = 150, id, location, onTransformationEnd } = props;

  return (
    <div className={getClass()} ref={ref}>
      <PlayList {...getProps()}>{children}</PlayList>
    </div>
  );

  function getProps() {
    return {
      className: getPlayListClass(),
      id,
      onPlayListComplete: onTransformationEnd,
      playList:
        location === "hidden" ? getOuterCloseTransformations() : getOuterOpenTransformations(),
    };
  }

  function getClass() {
    return classNames(className, styles.outerContainer, {
      [styles.backgroundAbove]: location === "above",
      [styles.backgroundBelow]: location === "below",
      [styles.backgroundLeft]: location === "left",
      [styles.backgroundRight]: location === "right",
    });
  }

  function getPlayListClass() {
    return classNames(styles.content, {
      [styles.contentOpen]: location !== "hidden",
    });
  }

  function getOuterOpenTransformations() {
    return [
      {
        duration,
        transformations: [getTranslateX(0), getTranslateY(0), getOpacity(1)],
      },
    ];
  }

  function getOuterCloseTransformations() {
    return [
      {
        duration: 100,
        transformations: [getTranslateX(1), getTranslateY(-2), getOpacity(0)],
      },
    ];
  }

  function getTranslateX(amount) {
    return {
      action: "translateX",
      amount: `${amount}px`,
    };
  }

  function getTranslateY(amount) {
    return {
      action: "translateY",
      amount: `${amount}px`,
    };
  }

  function getOpacity(amount) {
    return {
      action: "opacity",
      amount: `${amount}`,
    };
  }
});

Show.displayName = "Show";

Show.propTypes = {
  /** Content to be rendered. This is the content that Transform will act upon. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Length of animation for Show's render of its play list. */
  duration: PropTypes.number,

  /** Unique identifier which is passed to PlayList when it's necessary to use a new playList. */
  id: PropTypes.string,

  /** Required location where content is rendered once "opened". */
  location: adjoinPropTypes.location,

  /** Function called once Show's render animation is complete. */
  onTransformationEnd: PropTypes.func,
};
